import { rem } from 'polished'
import React, { useCallback } from 'react'
import styled from 'styled-components'

import PlannedBedbankOfferPackageSummary from './PlannedBedbankPackageSummary'
import PlannedLeOfferPackageSummary from './PlannedLeOfferPackageSummary'
import BookingsDetails from '../Common/BookingsDetails'
import ItemTypeAndBookedLabel from '../Common/ItemTypeAndBookedLabel'

import Hr from 'components/Common/Hr'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import ModalContent from 'LuxKit/Modal/ModalContent'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import offerPageURL from 'lib/offer/offerPageURL'
import { isBedbankOffer, isLEOffer } from 'lib/offer/offerTypes'
import BedbankOfferPackageSummary from 'tripPlanner/components/TripModal/TripItems/Summary/AccommodationSummary/BedbankOfferPackageSummary'
import LeOfferPackageSummary from 'tripPlanner/components/TripModal/TripItems/Summary/AccommodationSummary/LeOfferPackageSummary'
import AddressWithViewMapButton from 'tripPlanner/components/TripModal/TripItems/Summary/Common/AddressWithViewMapButton'
import CheckInAndOutDate from 'tripPlanner/components/TripModal/TripItems/Summary/Common/CheckInAndOutDate'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'
import { useOrder } from 'tripPlanner/hooks/api/order'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import type { AccommodationItem } from 'tripPlanner/types/tripItem'
import { getOrderId, isLEOfferNoSummary } from 'tripPlanner/utils'
import { useIntl } from 'react-intl'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  padding: 0 0 ${rem(12)} 0;
  border-bottom: 1px solid ${(props) => props.theme.palette.neutral.default.six};
`

const Section = styled.div`
  padding: ${rem(20)} 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  border-bottom: 1px solid ${(props) => props.theme.palette.neutral.default.six};
`

interface Props {
  item: AccommodationItem
  offer: App.Offer | App.BedbankOffer
}

function AccommodationItemSummaryContent({ item, offer }: Props) {
  const intl = useIntl()
  const { setImmersiveMode } = useImmersiveModeHandlers()
  const canEdit = useCanEditTrip()

  const onOfferLinkClick = useCallback(() => {
    if (canEdit) {
      setImmersiveMode({
        tripId: item.tripId,
        sectionId: tripItemSelectionId(item.id),
      })
    }
  }, [setImmersiveMode, item, canEdit])

  const haveAnyDates = item.startDate || item.endDate

  const orderId = getOrderId(item)

  const { data: order } = useOrder(orderId)

  const offerUrl = offer && offerPageURL(offer)

  const hasOfferDetails = offer && (isLEOffer(offer) || isBedbankOffer(offer))

  const willDisplayOfferAddress = hasOfferDetails && offer.property?.address
  const willDisplayItemAddress = !hasOfferDetails && item.place.address

  const plannedPackageId =
    'savedItemData' in item ? item.savedItemData?.packageId : undefined

  const lePlannedPackage =
    plannedPackageId &&
    !order &&
    isLEOfferNoSummary(offer) &&
    offer.packages.find((offerPackage) => offerPackage.id === plannedPackageId)
  const bedbankPlannedPackage =
    plannedPackageId &&
    !order &&
    !isLEOfferNoSummary(offer) &&
    offer.packages.find(
      (offerPackage) => offerPackage.roomId === plannedPackageId,
    )
  return (
    <ModalContent>
      <VerticalSpacer gap={20}>
        <ItemTypeAndBookedLabel item={item} />
        <Header>
          {offer && (
            <div>
              {offer?.type && (
                <ProductTypeLabel productType={offer.productType} />
              )}
            </div>
          )}
          {hasOfferDetails && (
            <BodyText variant="large">{offer?.name}</BodyText>
          )}
          {offerUrl && (
            <TextLink weight="bold" to={offerUrl} onClick={onOfferLinkClick}>
              {intl.formatMessage({
                id: 'Zg7zT_f2VHuSQohu2x9U',
                defaultMessage: 'View offer details',
                description: 'Link to view accommodation offer details in trip planner',
              })}
            </TextLink>
          )}
        </Header>
        {order && (
          <Section>
            <BookingsDetails order={order} orderItemType="offer" />
          </Section>
        )}
        {(haveAnyDates ||
          willDisplayOfferAddress ||
          willDisplayItemAddress) && (
          <Section>
            {haveAnyDates && (
              <CheckInAndOutDate
                isAccommodation
                startDate={item.startDate}
                endDate={item.endDate}
                startTime={item.startTime}
                endTime={item.endTime}
              />
            )}
            {!hasOfferDetails && item.place.address && (
              <AddressWithViewMapButton
                address={item.place.address}
                item={item}
              />
            )}
          </Section>
        )}
        {order && isLEOfferNoSummary(offer) && (
          <LeOfferPackageSummary offer={offer} order={order} />
        )}
        {order && !isLEOfferNoSummary(offer) && (
          <BedbankOfferPackageSummary offer={offer} order={order} />
        )}
        {lePlannedPackage &&
          'savedItemData' in item &&
          item.savedItemData?.occupancies?.map((_, index) => (
            <>
              {index !== 0 && <Hr colour="light-grey" />}
              <PlannedLeOfferPackageSummary
                key={index}
                offer={offer}
                pkg={lePlannedPackage}
              />
            </>
          ))}
        {bedbankPlannedPackage &&
          'savedItemData' in item &&
          item.savedItemData?.occupancies?.map((_, index) => (
            <>
              {index !== 0 && <Hr colour="light-grey" />}
              <PlannedBedbankOfferPackageSummary
                key={index}
                offer={offer}
                pkg={bedbankPlannedPackage}
              />
            </>
          ))}
      </VerticalSpacer>
    </ModalContent>
  )
}

export default AccommodationItemSummaryContent
